import {
  type CreateUpdateOrderDTO,
  Currency,
  type IngridAddress,
  type IngridSession,
  type OrderAddressDTO,
  type OrderDTO,
  OrderStatus,
  type PostNordAddress
} from 'ecosystem'
import { convertMinorUnitToValue, isNullable } from 'shared-utils'
import { transformOrderItemsToCreateOrderItems } from '../../../../utils/orders'
import { type OrderProvisionResult } from '../types'
import { getPostNordStreetsFromAddress } from '../utils'

export const generateBasePayload = ({
  customer: _customer,
  items,
  ...oldOrder
}: OrderDTO): CreateUpdateOrderDTO => {
  return {
    ...oldOrder,
    items: items ? transformOrderItemsToCreateOrderItems(items) : [],
    status: OrderStatus.PAID,
    currency: Currency.SEK
  }
}

export const generatePayloadFromIngrid = (
  basePayload: CreateUpdateOrderDTO,
  ingridResponse?: IngridSession
) => {
  if (!ingridResponse) {
    return basePayload
  }

  /* eslint-disable camelcase -- need */
  const { delivery_groups } = ingridResponse
  const deliveryGroup = delivery_groups[0]
  const deliveryAddress = deliveryGroup.addresses.delivery_address
  const shipping = deliveryGroup.shipping

  const generateOrderAddressFromIngridAddress = (
    address?: IngridAddress
  ): OrderAddressDTO | undefined => {
    return address
      ? {
          address1: address.address_lines?.join(', ') || '',
          address2: '',
          country: address.country || '',
          mobilePhone: deliveryAddress.phone || '',
          postalCode: address.postal_code || '',
          postalRegion: address.city || '',
          attention: `${deliveryAddress.first_name} ${deliveryAddress.last_name}`,
          reference: `${shipping.carrier} ${shipping.delivery_type}, ${shipping.product}`
        }
      : undefined
  }

  const recipientShippingAddress = generateOrderAddressFromIngridAddress(deliveryAddress)
  const pickupAddress = generateOrderAddressFromIngridAddress(
    deliveryGroup.addresses.location?.address
  )
  const shippingAddress = pickupAddress ?? recipientShippingAddress

  const carrierName = shipping.carrier
  const carrierService = shipping.product
  const trackingNumber = deliveryGroup.tos_id
  const pickupLocationId = deliveryGroup.addresses.location?.external_id
  const pickupLocationName = deliveryGroup.addresses.location?.name

  return {
    ...basePayload,
    shippingCost: convertMinorUnitToValue(deliveryGroup.pricing.price),
    ...(shippingAddress ? { shippingAddress } : {}),
    shippingInfo: {
      ...basePayload.shippingInfo,
      ...(carrierName ? { carrierName } : {}),
      ...(carrierService ? { carrierService } : {}),
      ...(trackingNumber ? { trackingNumber } : {}),
      ...(pickupAddress
        ? {
            pickupAddress,
            pickupLocationName,
            pickupLocationId
          }
        : {})
    }
  }
}

export const generatePayloadFromPostNord = (
  basePayload: CreateUpdateOrderDTO,
  postnordResponse?: Required<OrderProvisionResult>['shippingProviderResult']['postnordResponse']
): CreateUpdateOrderDTO => {
  if (!postnordResponse) {
    return basePayload
  }

  if (postnordResponse.isPickupFromStore) {
    return {
      ...basePayload,
      shippingCost: 0,
      shippingInfo: {
        ...basePayload.shippingInfo,
        additionalInfo: {
          postnordDetails: {
            isPickupFromStore: true
          }
        }
      }
    }
  }

  const generateOrderAddressFromPostnordAddress = (
    address?: PostNordAddress
  ): OrderAddressDTO | undefined => {
    return address
      ? {
          address1: getPostNordStreetsFromAddress(address).join(', '),
          address2: '',
          country: address.countryCode || '',
          mobilePhone: '',
          postalCode: address.postCode || '',
          postalRegion: address.city || '',
          attention: '',
          reference: `PostNord ${postnordResponse.deliveryOption?.type}`
        }
      : undefined
  }

  const recipientShippingAddress = generateOrderAddressFromPostnordAddress(
    postnordResponse.recipientAddress
  )
  const pickupAddress = generateOrderAddressFromPostnordAddress(
    postnordResponse.deliveryOption?.details.location?.address
  )
  const shippingAddress = pickupAddress ?? recipientShippingAddress

  const carrierService = postnordResponse.deliveryOption?.type
  const trackingNumber = postnordResponse.bookingDetails?.trackingNumber
  const trackingUrl = postnordResponse.bookingDetails?.trackingUrl
  const labelUrl = postnordResponse.bookingDetails?.labelUrl
  const pickupLocationId =
    postnordResponse.deliveryOption?.details.bookingInstructions.servicePointId
  const pickupLocationName = postnordResponse.deliveryOption?.details.location?.name
  const shippingCost = postnordResponse.deliveryOption
    ? (postnordResponse.deliveryOption?.price ?? 0)
    : undefined

  return {
    ...basePayload,
    ...(shippingAddress ? { shippingAddress } : {}),
    ...(!isNullable(shippingCost) ? { shippingCost } : {}),
    shippingInfo: {
      ...basePayload.shippingInfo,
      additionalInfo: {
        postnordDetails: postnordResponse
      },
      carrierName: 'PostNord',
      ...(carrierService ? { carrierService } : {}),
      ...(trackingNumber ? { trackingNumber } : {}),
      ...(trackingUrl ? { trackingUrl } : {}),
      ...(labelUrl ? { labelUrl } : {}),
      ...(pickupAddress
        ? {
            pickupAddress,
            pickupLocationName,
            pickupLocationId
          }
        : {})
    }
  }
}
